import {
  AutoPayStatus,
  BenefitDto,
  BenefitType,
  InitialPremiumPaymentStatus,
  OnboardingPeriodDto,
} from '@zorro/clients';
import { Divider } from '@zorro/design-system/v2/Divider';
import { Drawer } from '@zorro/design-system/v2/Drawer';
import { ModalMessage } from '@zorro/design-system/v2/ModalMessage';
import { Select } from '@zorro/design-system/v2/Select';
import { Title } from '@zorro/design-system/v2/Title';
import { Stack } from '@zorro/web/design-system';
import {
  isZorroManagedEnrollmentTeam,
  useMonolithMutation,
  useMonolithQuery,
} from '@zorro/web/ui-utils';
import { ReactNode, useState } from 'react';

import { useAnalytics } from '../../Analytics';
import { FormFooter } from '../../FormFooter/FormFooter';
import {
  EmployeePaymentStatusDashboardVariant,
  EmployeePaymentStatusDefaultVariant,
  EmployeePaymentStatusElenaVariant,
  PaymentStatusData,
} from './EmployeePaymentStatusVariants';
import { ManagePaymentStatus } from './ManagePaymentStatus';
import {
  getAutoPayStatus,
  getInitialPremiumStatus,
  getMonthlyAutoPayDateOptions,
  parseMonthlyAutoPayDateFromOptions,
} from './PaymentStatusUtils';

type PaymentStatusVariant = 'default' | 'elena' | 'dashboard';

type EmployeePaymentStatusProps = {
  onboardingPeriodId: OnboardingPeriodDto['id'];
  shouldShowCompletedStatus?: boolean;
  shouldShowDivider?: boolean;
  shouldMakeVariantOutline?: boolean;
  variant?: PaymentStatusVariant;
  shouldShowManageButton?: boolean;
  children?: ReactNode;
};

const defaultStatusData: PaymentStatusData = {
  label: '-',
  icon: null,
  description: null,
  learnMoreLink: null,
  toolTipLabel: null,
  shouldShowMarkAsDone: false,
};

type BenefitPaymentStatusProps = {
  benefit: BenefitDto;
  variant: PaymentStatusVariant;
  shouldShowCompletedStatus?: boolean;
  shouldShowDivider?: boolean;
  shouldMakeVariantOutline?: boolean;
  children?: ReactNode;
};

const BenefitPaymentStatus = ({
  benefit,
  variant,
  shouldShowCompletedStatus,
  shouldShowDivider,
  shouldMakeVariantOutline,
  children,
}: BenefitPaymentStatusProps) => {
  const { sendEvent } = useAnalytics();
  const [isPremiumPaymentModalOpen, setIsPremiumPaymentModalOpen] =
    useState<boolean>(false);
  const [isAutoPayDrawerOpen, setIsAutoPayDrawerOpen] =
    useState<boolean>(false);
  const [selectedAutoPayDate, setSelectedAutoPayDate] = useState<string | null>(
    null
  );

  const { tryMutate: updatePaymentStatus } = useMonolithMutation({
    method: 'benefitsControllerUpdatePaymentStatus',
  });

  const {
    initialPremiumPaymentStatus,
    autoPayStatus,
    monthlyAutoPayDate,
    isMonthlyAutoPayDateEndOfMonth,
  } = benefit;

  const markPremiumPaymentDone = async () => {
    void sendEvent('initial_payment_done', { benefit_id: benefit.id }).catch(
      () => undefined
    );
    await updatePaymentStatus([
      benefit.id,
      {
        initialPremiumPaymentStatus:
          InitialPremiumPaymentStatus.SET_UP_BY_EMPLOYEE,
      },
    ]);
    setIsPremiumPaymentModalOpen(false);
  };

  const markAutoPayDone = async () => {
    void sendEvent('auto_payment_done', { benefit_id: benefit.id }).catch(
      () => undefined
    );
    await updatePaymentStatus([
      benefit.id,
      {
        autoPayStatus: AutoPayStatus.SET_UP_BY_EMPLOYEE,
        ...parseMonthlyAutoPayDateFromOptions(selectedAutoPayDate),
      },
    ]);
    setIsAutoPayDrawerOpen(false);
    setSelectedAutoPayDate(null);
  };

  const MONTHLY_AUTO_PAY_DATE_DATA = getMonthlyAutoPayDateOptions();

  const isDashboardVariant = variant === 'dashboard';
  const shouldShowMarkAsDone = variant === 'elena' || isDashboardVariant;

  const hasAgentHandledStatus =
    initialPremiumPaymentStatus ===
      InitialPremiumPaymentStatus.NOT_SET_UP_BY_AGENT ||
    autoPayStatus === AutoPayStatus.NOT_SET_UP_BY_AGENT;

  const { data: onboardingPeriodReport } = useMonolithQuery({
    method: 'onboardingPeriodReportsControllerGenerateOnboardingPeriodReport',
    params: shouldShowMarkAsDone && hasAgentHandledStatus && [benefit.periodId],
  });

  const isZorroEnrollmentTeam = isZorroManagedEnrollmentTeam(
    onboardingPeriodReport?.enrollmentTeamId
  );

  const premiumPaymentData = initialPremiumPaymentStatus
    ? getInitialPremiumStatus(
        initialPremiumPaymentStatus,
        shouldShowMarkAsDone,
        isZorroEnrollmentTeam
      )
    : defaultStatusData;

  const autoPayData = autoPayStatus
    ? getAutoPayStatus(
        autoPayStatus,
        shouldShowMarkAsDone,
        isZorroEnrollmentTeam
      )
    : defaultStatusData;

  const shouldShowDashboardTitle =
    isDashboardVariant &&
    (premiumPaymentData.shouldShowMarkAsDone ||
      autoPayData.shouldShowMarkAsDone);

  const renderVariantContent = () => {
    switch (variant) {
      case 'elena':
        return (
          <EmployeePaymentStatusElenaVariant
            premiumPaymentData={premiumPaymentData}
            autoPayData={autoPayData}
            onPremiumMarkAsDone={() => setIsPremiumPaymentModalOpen(true)}
            onAutoPayMarkAsDone={() => setIsAutoPayDrawerOpen(true)}
            children={children}
          />
        );

      case 'dashboard':
        return (
          <EmployeePaymentStatusDashboardVariant
            premiumPaymentData={premiumPaymentData}
            autoPayData={autoPayData}
            onPremiumMarkAsDone={() => setIsPremiumPaymentModalOpen(true)}
            onAutoPayMarkAsDone={() => setIsAutoPayDrawerOpen(true)}
            shouldShowCompletedStatus={shouldShowCompletedStatus}
            children={children}
            shouldMakeVariantOutline={shouldMakeVariantOutline}
            onboardingPeriodId={benefit.periodId}
          />
        );

      default:
        return (
          <EmployeePaymentStatusDefaultVariant
            premiumPaymentData={premiumPaymentData}
            autoPayData={autoPayData}
            monthlyAutoPayDate={monthlyAutoPayDate}
            isMonthlyAutoPayDateEndOfMonth={isMonthlyAutoPayDateEndOfMonth}
            children={children}
          />
        );
    }
  };

  return (
    <Stack gap="sm">
      {isDashboardVariant ? (
        shouldShowDashboardTitle && (
          <>
            {shouldShowDivider && <Divider mt="xl" pb="md" />}
            <Title order={2} size="sm">
              Payment status
            </Title>
          </>
        )
      ) : (
        <Title order={2} size="sm">
          Payment status
        </Title>
      )}

      {renderVariantContent()}

      <ModalMessage
        isOpen={isPremiumPaymentModalOpen}
        onClose={() => setIsPremiumPaymentModalOpen(false)}
        variant="regular"
        title="Confirm initial premium setup"
        description="Are you sure you have set up your initial premium payment? Confirm to update your status."
        primaryLabel="Confirm"
        onPrimaryAction={markPremiumPaymentDone}
        secondaryLabel="Cancel"
        onSecondaryAction={() => setIsPremiumPaymentModalOpen(false)}
      />

      <Drawer
        opened={isAutoPayDrawerOpen}
        onClose={() => {
          setIsAutoPayDrawerOpen(false);
          setSelectedAutoPayDate(null);
        }}
        title="Confirm auto pay"
        position="right"
        footer={
          <FormFooter
            withPaper={false}
            primaryLabel="Confirm"
            primaryButtonProps={{
              onClick: markAutoPayDone,
              disabled: !selectedAutoPayDate,
            }}
            secondaryLabel="Cancel"
            secondaryButtonProps={{
              onClick: () => {
                setIsAutoPayDrawerOpen(false);
                setSelectedAutoPayDate(null);
              },
            }}
          />
        }
      >
        <Select
          label="Monthly auto-pay date"
          placeholder="Select monthly auto-pay date"
          data={MONTHLY_AUTO_PAY_DATE_DATA}
          value={selectedAutoPayDate}
          onChange={(val) => setSelectedAutoPayDate(val)}
        />
      </Drawer>
    </Stack>
  );
};

export const EmployeePaymentStatus = ({
  onboardingPeriodId,
  variant = 'default',
  shouldShowCompletedStatus,
  shouldShowDivider,
  shouldMakeVariantOutline,
  shouldShowManageButton,
  children,
}: EmployeePaymentStatusProps) => {
  const { isLoading, data: benefits } = useMonolithQuery({
    method: 'benefitsControllerGetBenefits',
    params: [onboardingPeriodId],
  });

  if (isLoading || !benefits) {
    return null;
  }

  // will render per-benefit once supplemental/multi-plan ui support is added
  const majorMedicalBenefit = benefits.allBenefits.find(
    (benefit) =>
      benefit.type === BenefitType.MAJOR_MEDICAL &&
      (benefit.autoPayStatus || benefit.initialPremiumPaymentStatus)
  );

  if (!majorMedicalBenefit) {
    return null;
  }

  return (
    <BenefitPaymentStatus
      benefit={majorMedicalBenefit}
      variant={variant}
      shouldShowCompletedStatus={shouldShowCompletedStatus}
      shouldShowDivider={shouldShowDivider}
      shouldMakeVariantOutline={shouldMakeVariantOutline}
    >
      {shouldShowManageButton && (
        <ManagePaymentStatus
          benefitId={majorMedicalBenefit.id}
          onboardingPeriodId={majorMedicalBenefit.periodId}
        />
      )}
      {children}
    </BenefitPaymentStatus>
  );
};
